import { isEmpty } from 'lodash';
import React, { useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { BsPlayCircleFill } from 'react-icons/bs';

import BayModal from '../../BayModal';
import { Previewer, PreviewerProps } from '../../BayPolotno/Previewer';

export type PreviewMode = 'polotno' | 'raster' | 'media';

export interface PreviewMedia {
	url: string;
	mimeType?: string;
	posterUrl?: string;
	name?: string;
}

export interface PreviewModalProps {
	show: boolean;
	onHide: () => void;
	footer: React.ReactNode;
	t: (key: string, defaultVal?: string) => string;
	mode: PreviewMode;
	template?: any;
	previewer?: Omit<PreviewerProps, 'template'>;
	rasterUrl?: string | null;
	rasterKind?: 'email' | 'block';
	media?: PreviewMedia;
	id?: string;
	title?: React.ReactNode;
	size?: 'lg' | 'xl';
}

const findRaster = (rasters: any, formatKey: string): string | undefined =>
	Array.isArray(rasters)
		? rasters.find((r: any) => r?.formatKey === formatKey)?.url
		: undefined;

// The backend emits a single full-resolution raster under `thumbnail` (no
// `fullSize`), so thumbnail is an accepted fallback. auxRasters are the legacy
// client-generated rasters for templates saved before the backend switch.
export const getTemplateRasterUrl = (template: any): string | null =>
	findRaster(template?.rasters, 'fullSize') ||
	findRaster(template?.rasters, 'thumbnail') ||
	findRaster(template?.etc?.auxRasters, 'fullSize') ||
	findRaster(template?.etc?.auxRasters, 'thumbnail') ||
	null;

export const isVideoMedia = (media?: PreviewMedia): boolean => {
	if (!media) return false;
	if (media.mimeType) return media.mimeType.startsWith('video/');
	const extension = media.url?.split('?')[0].split('.').pop()?.toLowerCase();
	return extension === 'mp4' || extension === 'mov';
};

const RasterBody: React.FC<{
	template?: any;
	rasterUrl?: string | null;
	rasterKind?: 'email' | 'block';
	t: PreviewModalProps['t'];
}> = ({ template, rasterUrl, rasterKind, t }) => {
	const isBlock = rasterKind === 'block';

	if (!rasterUrl) {
		return (
			<Container
				className="d-flex align-items-center justify-content-center"
				style={{ height: '65vh', minHeight: 320 }}
			>
				<div className="text-center text-muted">
					<p>{t('Preview not available')}</p>
					<small>
						{isBlock
							? t('Block preview will be generated after saving')
							: t('Email preview will be generated after saving')}
					</small>
				</div>
			</Container>
		);
	}

	return (
		<Container
			style={{
				maxHeight: 'calc(100vh - 220px)',
				minHeight: 320,
				overflow: 'auto',
			}}
		>
			{/* A block is a fragment capped at the email body width; a template is a
			    full 600px-wide document. */}
			<div
				style={
					isBlock
						? { maxWidth: '600px', margin: '0 auto' }
						: { width: '600px', margin: '0 auto' }
				}
			>
				{isBlock && template?.name && (
					<div className="fw-bold mb-3">{template.name}</div>
				)}
				<img
					className="rounded"
					src={rasterUrl}
					crossOrigin="anonymous"
					alt={template?.name ? `${template.name} ${t('preview')}` : t('preview')}
					style={
						isBlock
							? { display: 'block', width: '100%', height: 'auto' }
							: { display: 'block', width: '600px', height: 'auto' }
					}
				/>
			</div>
		</Container>
	);
};

const MediaBody: React.FC<{
	media?: PreviewMedia;
	videoRef: React.RefObject<HTMLVideoElement>;
	playing: boolean;
	setPlaying: (value: boolean) => void;
	t: PreviewModalProps['t'];
}> = ({ media, videoRef, playing, setPlaying, t }) => {
	if (isVideoMedia(media)) {
		return (
			<Container className="d-flex align-items-center justify-content-center">
				<div
					style={{
						width: '100%',
						maxWidth: '720px',
						margin: '0 auto',
						position: 'relative',
						paddingBottom: '56.25%',
						height: 0,
						overflow: 'hidden',
					}}
				>
					{!playing && (
						<button
							type="button"
							aria-label={t('play')}
							data-testid="preview-play-overlay"
							onClick={() => {
								if (videoRef.current) {
									videoRef.current.play();
									setPlaying(true);
								}
							}}
							style={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: '100%',
								height: '100%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								zIndex: 10,
								cursor: 'pointer',
								border: 0,
								padding: 0,
								background: 'rgba(0, 0, 0, 0.1)',
							}}
						>
							<BsPlayCircleFill
								size={80}
								color="white"
								style={{
									filter: 'drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.5))',
									opacity: 0.9,
								}}
							/>
						</button>
					)}
					<video
						ref={videoRef}
						data-testid="preview-video"
						src={media?.url}
						controls={playing}
						crossOrigin="anonymous"
						poster={media?.posterUrl}
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '100%',
							objectFit: 'contain',
						}}
						onPlay={() => setPlaying(true)}
						onPause={() => {
							if (videoRef.current && videoRef.current.currentTime === 0) {
								setPlaying(false);
							}
						}}
						onEnded={() => setPlaying(false)}
					>
						{t('Your browser does not support the video tag.')}
					</video>
				</div>
			</Container>
		);
	}

	return (
		<Container
			className="d-flex align-items-center justify-content-center mb-3"
			style={{ minHeight: 320, maxHeight: 540 }}
		>
			<div
				className="d-flex align-items-center justify-content-center"
				style={{ position: 'relative', width: '80%', height: 'auto' }}
			>
				<img
					crossOrigin="anonymous"
					src={media?.url}
					className="img-fluid"
					alt={media?.name ? `${media.name} ${t('preview')}` : t('preview')}
					style={{ objectFit: 'contain', height: '100%', maxHeight: 580 }}
				/>
			</div>
		</Container>
	);
};

export const PreviewModal: React.FC<PreviewModalProps> = ({
	show,
	onHide,
	footer,
	t,
	mode,
	template,
	previewer,
	rasterUrl,
	rasterKind,
	media,
	id = 'template_preview_modal',
	title,
	size = 'lg',
}) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const [playing, setPlaying] = useState(false);

	const handleHide = () => {
		videoRef.current?.pause();
		setPlaying(false);
		onHide();
	};

	const hasDesign = !isEmpty(template?.json ?? template?.source?.data);

	return (
		<BayModal
			title={title ?? t('preview')}
			size={size}
			centered
			closeButton
			noPadding
			height="100%"
			t={t}
			id={id}
			show={show}
			footer={footer}
			onHideFunc={handleHide}
		>
			{show && mode === 'polotno' && hasDesign && previewer && (
				<Previewer template={template} {...previewer} />
			)}
			{show && mode === 'raster' && (
				<RasterBody
					template={template}
					rasterUrl={rasterUrl}
					rasterKind={rasterKind}
					t={t}
				/>
			)}
			{show && mode === 'media' && (
				<MediaBody
					media={media}
					videoRef={videoRef}
					playing={playing}
					setPlaying={setPlaying}
					t={t}
				/>
			)}
		</BayModal>
	);
};

export default PreviewModal;
